import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Input, Modal, Spin, Tag } from 'antd';
import {
  assignAhriProduct,
  FIELD_LABELS,
  getAhriSystemComponents,
  searchProductsForField,
  unassignAhriProduct,
} from './api';

const FIELDS = ['indoorModelNumber', 'outdoorModelNumber', 'furnaceModelNumber'];

const ProductRow = ({ name, code, mpn, imageUrl, right }) => (
  <div className="d-flex gap-3 align-items-center p-2 rounded border">
    <div
      className="bg-light d-flex align-items-center justify-content-center rounded flex-shrink-0 overflow-hidden"
      style={{ width: 48, height: 48 }}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      ) : (
        <span className="text-muted" style={{ fontSize: 9 }}>No image</span>
      )}
    </div>
    <div className="flex-grow-1" style={{ minWidth: 0 }}>
      <div className="fw-semibold text-truncate">{code}</div>
      <div className="text-muted small text-truncate">{name || 'Unnamed product'}</div>
      {mpn && <div className="text-muted small">MFR #: {mpn}</div>}
    </div>
    {right}
  </div>
);

const matchStatusTag = (complete, partial) => {
  if (complete) return <Tag color="green">Complete match</Tag>;
  if (partial) return <Tag color="gold">Partial match</Tag>;
  return <Tag color="red">No match</Tag>;
};

const FieldSection = ({
  field,
  modelNumber,
  assigned,
  onAssign,
  onUnassign,
  getAuthHeaders,
  busy,
}) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [results, setResults] = useState([]);
  const [onCategoryCount, setOnCategoryCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  const runSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSearchError('');
    setHasSearched(true);
    try {
      const { docs, onCategoryCount: onCat } = await searchProductsForField(field, q, getAuthHeaders);
      setResults(docs);
      setOnCategoryCount(onCat);
    } catch (err) {
      setSearchError(err?.message || 'Search failed');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="fw-semibold text-uppercase small text-muted">{FIELD_LABELS[field]}</div>
        <div className="text-muted small">AHRI model: {modelNumber || '-'}</div>
      </div>

      <div className="d-flex flex-column gap-2 mb-2">
        {assigned.length === 0 && <div className="text-muted small">No product assigned yet.</div>}
        {assigned.map((opt) => (
          <ProductRow
            key={opt.product_code}
            code={opt.product_code}
            name={opt.name}
            mpn={opt.manufacturer_part_number}
            imageUrl={opt.image_url}
            right={
              <Button
                size="small"
                danger
                loading={busy === `unassign:${opt.product_code}`}
                onClick={() => onUnassign(field, opt.product_code)}
              >
                Remove
              </Button>
            }
          />
        ))}
      </div>

      <Input.Search
        placeholder="Search by part number, name, manufacturer..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onSearch={runSearch}
        loading={searching}
        enterButton="Search"
        allowClear
      />

      <div className="mt-2">
        {searching && (
          <div className="text-center py-2">
            <Spin size="small" />
          </div>
        )}
        {!searching && searchError && <Alert type="error" showIcon message={searchError} />}
        {!searching && !searchError && hasSearched && results.length === 0 && (
          <Empty description="No products found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
        {!searching && results.length > 0 && (
          <div className="d-flex flex-column gap-2">
            {onCategoryCount > 0 && onCategoryCount < results.length && (
              <div className="text-muted small">Showing likely {FIELD_LABELS[field].toLowerCase()} matches first.</div>
            )}
            {results.map((doc, idx) => (
              <ProductRow
                key={doc.productCode || idx}
                code={doc.productCode}
                name={doc.name}
                mpn={doc.manufacturerPartNumber}
                imageUrl={doc.imageUrl}
                right={
                  <Button
                    size="small"
                    type="primary"
                    loading={busy === `assign:${doc.productCode}`}
                    onClick={() => onAssign(field, doc.productCode)}
                  >
                    Assign
                  </Button>
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AssignAhriModelModal = ({ open, onClose, ahriId, rowJson, initialCompleteMatch, getAuthHeaders, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [slotsByField, setSlotsByField] = useState({});
  const [status, setStatus] = useState({ complete: Boolean(initialCompleteMatch), partial: false, known: false });
  const [busy, setBusy] = useState('');

  const loadComponents = useCallback(async () => {
    if (!ahriId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getAhriSystemComponents(ahriId, getAuthHeaders);
      const byField = {};
      (data?.slots || []).forEach((slot) => {
        byField[slot.field] = slot.options || [];
      });
      setSlotsByField(byField);
    } catch (err) {
      setError(err?.message || 'Failed to load current assignments');
    } finally {
      setLoading(false);
    }
  }, [ahriId, getAuthHeaders]);

  useEffect(() => {
    if (!open) return;
    setStatus({ complete: Boolean(initialCompleteMatch), partial: false, known: false });
    loadComponents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ahriId]);

  const applyStatus = (result) => {
    if (!result) return;
    const next = { complete: Boolean(result.complete_match), partial: Boolean(result.partial_match), known: true };
    setStatus(next);
    onStatusChange?.(next);
  };

  const handleAssign = async (field, productCode) => {
    setBusy(`assign:${productCode}`);
    setError('');
    try {
      const result = await assignAhriProduct(ahriId, field, productCode, getAuthHeaders);
      applyStatus(result);
      await loadComponents();
    } catch (err) {
      setError(err?.message || 'Failed to assign product');
    } finally {
      setBusy('');
    }
  };

  const handleUnassign = async (field, productCode) => {
    setBusy(`unassign:${productCode}`);
    setError('');
    try {
      const result = await unassignAhriProduct(ahriId, field, productCode, getAuthHeaders);
      applyStatus(result);
      await loadComponents();
    } catch (err) {
      setError(err?.message || 'Failed to remove product');
    } finally {
      setBusy('');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      title={`Assign Models — ${ahriId || ''}`}
      width={720}
      destroyOnClose
    >
      <div className="mb-3">
        {status.known ? matchStatusTag(status.complete, status.partial) : matchStatusTag(status.complete, false)}
      </div>

      {error && <Alert className="mb-3" type="error" showIcon message={error} />}

      {loading ? (
        <div className="text-center py-4">
          <Spin />
        </div>
      ) : (
        FIELDS.map((field) => (
          <FieldSection
            key={field}
            field={field}
            modelNumber={rowJson?.[field]}
            assigned={slotsByField[field] || []}
            onAssign={handleAssign}
            onUnassign={handleUnassign}
            getAuthHeaders={getAuthHeaders}
            busy={busy}
          />
        ))
      )}
    </Modal>
  );
};

export default AssignAhriModelModal;
